import React, { useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, useSearchParams } from 'react-router-dom';

// Redirects to conversation-interface.html (the static HTML that runs the
// Q&A flow). Critical invariant: the redirect URL MUST include userId, or
// conversation-interface.html falls into local-only fallback mode —
// bypassing the conversation-manager API, skipping all DB persistence,
// losing every answer on refresh, and generating a template eulogy with
// no reference to the user's answers. That was the Marjorie Cooper bug
// (April 2026 video call with Rochelle).

const ConversationPage: React.FC = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();

  const type = searchParams.get('type') || 'eulogy';
  const name = searchParams.get('name') || '';
  const relationship = searchParams.get('relationship') || '';
  const sessionId = searchParams.get('sessionId') || '';
  const autoAdvance = searchParams.get('autoAdvance') || '';

  // Hook registered on every render (before any early returns) — fixes the
  // previous hooks-rule violation that caused the effect to fire before
  // Clerk had populated `user`, stripping userId from the redirect URL.
  useEffect(() => {
    // Wait for Clerk to fully hydrate. On a fresh browser returning from
    // Stripe checkout, there's a window where isLoaded+isSignedIn have
    // flipped true but `user` is still null. Redirecting in that window
    // drops the user into fallback mode.
    if (!isLoaded || !isSignedIn || !user?.id) {
      return;
    }

    async function ensureProfileAndRedirect() {
      try {
        const token = await getToken();
        if (token) {
          const resp = await fetch('/api/user/ensure-profile', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            console.warn('ensure-profile returned error:', resp.status, body);
          }
        }
      } catch (err) {
        console.warn('ensure-profile failed (non-fatal):', err);
      }

      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);
      if (type) params.set('type', type);
      if (name) params.set('name', name);
      if (relationship) params.set('relationship', relationship);
      if (autoAdvance) params.set('autoAdvance', autoAdvance);

      // userId is the critical parameter — guaranteed non-empty here
      // because of the guard above.
      params.set('userId', user!.id);
      const userName = [user!.firstName, user!.lastName].filter(Boolean).join(' ');
      if (userName) params.set('userName', userName);
      const userEmail = user!.primaryEmailAddress?.emailAddress || '';
      if (userEmail) params.set('userEmail', userEmail);

      window.location.href = `/conversation-interface.html?${params.toString()}`;
    }

    ensureProfileAndRedirect();
  }, [isLoaded, isSignedIn, user, getToken, type, name, relationship, sessionId, autoAdvance]);

  // Only redirect to sign-in once Clerk has finished loading and confirmed
  // the user is not signed in — never during the initial loading window.
  if (isLoaded && !isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Loading view (shown while Clerk hydrates and while the async
  // ensureProfile + redirect runs).
  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-background">
      <div className="text-center">
        <img src="/Assets/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mx-auto mb-4 animate-pulse" />
        <p className="text-cool-gray">Loading conversation interface...</p>
      </div>
    </div>
  );
};

export default ConversationPage;
